import { useState, useEffect, useRef } from "react"
import { IconButton } from "@mui/material"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import { H5, Body3 } from "../../themes/typography"
import "../../styles/event/DateSelection.css"

/**
 * DateSelection
 * -----------------------------------------------------------------------------
 * A horizontal, scrollable date picker that supports selecting a date RANGE
 * within the currently visible month. It exposes two callbacks:
 *  - onDateChange(startDate, endDate)
 *  - onMonthChange(dateForCurrentMonth)
 *
 * Selection behavior:
 *  - 1st click: sets both start and end to the clicked day
 *  - 2nd click: sets the end day (range). If you click a day before the start,
 *               it becomes the new start and the previous start becomes the end
 *  - Clicking the same selected day again clears the selection
 *
 * Disabled behavior:
 *  - Past dates (before today at 00:00) are disabled
 *
 * Visuals:
 *  - Weekends use a secondary color
 *  - Today is highlighted
 *  - Selected date range is highlighted
 */
export default function DateSelection({
    onDateChange = () => { },
    onMonthChange = () => { }
}) {

    // To track the current month
    const [currentMonth, setCurrentMonth] = useState(new Date())


    /**
    * selectedDate is an array [startDate, endDate] containing two elements. Either can be null.
    * We store full Date objects (normalized to midnight when compared).
    */
    const [selectedDate, setSelectedDate] = useState([null, null])
    // console.log("Selected Date:", selectedDate)


    /**
   * clickCount controls the selection flow:
   * 0 = no selection yet
   * 1 = first click done (start selected)
   * 2 = range completed
   */
    const [clickCount, setClickCount] = useState(0)


    /**
   * showScrollButtons is derived UI state that becomes true when
   * the date row overflows horizontally. Currently used for conditional
   * button visibility (the classNames are present but commented out).
   */
    const [showScrollButtons, setShowScrollButtons] = useState(false)


    // Ref to the horizontally scrollable container of date cells
    const dateContainerRef = useRef(null)


    // Compute number of days in the visible month
    const daysInMonth = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        0
    ).getDate()


    // Weekday labels (starts with Sunday)
    const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]


    /**
   * formatDate
   * Formats a Date object into YYYY{sep}MM{sep}DD. Returns empty string if null.
   */
    function formatDate(date, separator = "/") {
        if (!date) return ""
        const day = String(date.getDate()).padStart(2, "0")
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const year = date.getFullYear()
        return `${year}${separator}${month}${separator}${day}`
    }


    /**
   * handleDateClick
   * Implements range selection with 1st/2nd click semantics.
   * Prevents selecting past dates by comparing against today at 00:00.
   */
    function handleDateClick(day) {
        const clickedDate = new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth(),
            day
        )

        // Normalize "today" to midnight to avoid time-of-day issues
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Disallow selecting any date strictly before today (means in past)
        if (clickedDate < today) return

        if (clickCount === 0 || clickCount === 2) {
            // Start a new selection cycle

            if (selectedDate[0] !== null && clickedDate.getTime() === selectedDate[0].getTime()) {
                // Clicking the same day toggles the selection off
                setSelectedDate([null, null])
                setClickCount(0)
            } else {
                // First click: start = end = clicked day (single-day selection)
                setSelectedDate([clickedDate, clickedDate])
                setClickCount(1)
            }

        } else if (clickCount === 1) {
            // Second click completes the range

            if (selectedDate[0] !== null && clickedDate.getTime() === selectedDate[0].getTime()) {
                // Clicking the same start day clears selection
                setSelectedDate([null, null])
                setClickCount(0)
            } else if (clickedDate.getTime() < selectedDate[0].getTime()) {
                // If the second click is before the start, swap
                setSelectedDate([clickedDate, selectedDate[0]])
                setClickCount(2)
            } else {
                // Normal case: extend to the right
                setSelectedDate([selectedDate[0], clickedDate])
                setClickCount(2)
            }
        }
    }

    /**
       * Side effect: notify parent about month changes and date range changes.
       * - onMonthChange receives the Date representing the current month view
       * - onDateChange receives formatted strings once both dates are selected;
       *   otherwise it receives the raw values (null or Date).
       */
    useEffect(() => {
        if (selectedDate[0] && selectedDate[1]) {
            // Emit formatted strings when range is fully selected
            onDateChange(formatDate(selectedDate[0], "-"), formatDate(selectedDate[1], "-"))
        } else {
            // Emit raw values during partial selection
            onDateChange(selectedDate[0], selectedDate[1])
        }
    }, [selectedDate])


    /**
   * Side effect: notify parent about month changes.
   * - onMonthChange receives the Date representing the current month view
   */
    useEffect(() => {
        if (onMonthChange) {
            onMonthChange(currentMonth)
        }
    }, [currentMonth])


    /**
   * isDateInRange
   * Returns true if the given day falls within [start, end] (inclusive)
   * for the current month.
   */
    function isDateInRange(day) {
        if (!selectedDate[0] || !selectedDate[1]) return false
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
        return date >= selectedDate[0] && date <= selectedDate[1]
    }


    /**
  * isDateDisabled
  * Returns true if the given day is before today (midnight).
  */
    function isDateDisabled(day) {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return date < today
    }


    /**
   * isPresentDate
   * Returns true if the given day is exactly today (midnight equality).
   */
    function isPresentDate(day) {
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        return date.getTime() === today.getTime()
    }


    /**
   * handleDateScroll
   * Scrolls the horizontal list of dates left/right by 100px.
   */
    function handleDateScroll(direction) {
        if (dateContainerRef.current) {
            dateContainerRef.current.scrollBy({
                left: direction === "left" ? -200 : 200,
                behavior: "smooth",
            })
        }
    }

    /**
   * handleChangeMonth
   * Moves the visible month backward/forward by `increment` months.
   * Uses day = 1 to avoid month overflow issues (e.g., Jan 31 -> Feb).
   */
    function handleChangeMonth(increment) {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1))
    }


    /**
  * Effect: detect horizontal overflow to optionally show scroll buttons.
  * Re-runs on month change (which changes the number of days and layout),
  * and on window resize.
  */
    useEffect(() => {
        function checkOverflow() {
            if (dateContainerRef.current) {
                setShowScrollButtons(dateContainerRef.current.scrollWidth > dateContainerRef.current.clientWidth)
            }
        }

        checkOverflow()
        window.addEventListener("resize", checkOverflow)
        return () => window.removeEventListener("resize", checkOverflow)
    }, [currentMonth])

    return (
        <div className="date-selection-picker-container">
            <div className="date-selection-picker-main-container">
                <div className="month-selector-header">
                    <div className="month-change-controls">
                        {/* Disable if already at current month */}
                        <IconButton
                            onClick={() => handleChangeMonth(-1)}
                            disabled={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
                            sx={{
                                '&.Mui-disabled': {
                                    opacity: 0.6,
                                    color: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }}
                        >
                            <ChevronLeft />
                        </IconButton>

                        <div className="date-selection-month-with-year">
                            <H5>
                                {currentMonth.toLocaleString("default", {
                                    month: "long",
                                    year: "numeric",
                                })}
                            </H5>
                        </div>

                        <IconButton
                            onClick={() => handleChangeMonth(1)}
                            sx={{
                                '&.Mui-disabled': {
                                    opacity: 0.6,
                                    color: 'var(--quaternary-color)',
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all'
                                }
                            }}
                        >
                            <ChevronRight />
                        </IconButton>
                    </div>
                </div>

                <div className="date-listing-wrapper">
                    <IconButton
                        // className={`dates-scroll-button v1-scroll-left ${showScrollButtons ? "visible" : ""}`}
                        onClick={() => handleDateScroll("left")}
                        sx={leftRightBtn}
                    >
                        <ChevronLeft />
                    </IconButton>

                    <div className="date-list-container" ref={dateContainerRef}>
                        <div className="date-listing-container">
                            {Array.from({ length: daysInMonth }).map((_, index) => {
                                const day = index + 1
                                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
                                const isWeekend = date.getDay() === 0 || date.getDay() === 6
                                const isToday = isPresentDate(day)
                                const isDisabled = isDateDisabled(day)
                                const isInRange = isDateInRange(day)
                                const dayColor = isWeekend ? 'var(--secondary-color)' : 'var(--quaternary-color)'

                                if (isDisabled) return null

                                return (
                                    <div className="each-date-cell" key={day}>
                                        <Body3
                                            style={{ color: dayColor }}
                                            fontWeight={400}
                                        >
                                            {days[date.getDay()]}
                                        </Body3>
                                        <button
                                            className={`each-date-button ${isToday ? "today" : ""} ${isInRange ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                                            onClick={() => handleDateClick(day)}
                                            disabled={isDisabled}
                                        >
                                            {day}
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <IconButton
                        // className={`dates-scroll-button v1-scroll-right ${showScrollButtons ? "visible" : ""}`}
                        onClick={() => handleDateScroll("right")}
                        sx={leftRightBtn}
                    >
                        <ChevronRight />
                    </IconButton>
                </div>
            </div>
        </div>
    )
}

const leftRightBtn = {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    marginTop: '1.25rem',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    padding: '4px'
}